import { FC, MouseEvent, PropsWithChildren } from 'react';
import { Card, makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

export type TBaseCardProps = PropsWithChildren<{
  /**
   * If true, the card can be selected. If false, no selection highlight or callbacks.
   */
  selectable?: boolean;
  /**
   * Current selection state. If `selectable` is false, this has no effect.
   */
  selected?: boolean;
  /**
   * Fired when the user clicks the card (unless the click is ignored via ignoreClickSelectors).
   * Usually used to set/unset `selected`.
   */
  onSelectionChange?: (isSelected: boolean, event: MouseEvent<HTMLElement>) => void;
  /**
   * Class or classes (e.g., .mySuggestionPopup) on child elements where clicks should NOT
   * toggle selection. Example: mention suggestions, popovers, etc.
   */
  ignoreClickSelectors?: string[];
  className?: string;
}>;

const useStyles = makeStyles({
  baseCard: {
    width: '100%',
    maxWidth: '100%',
    outline: `1px solid ${tokens.colorNeutralStroke3}`,
    backgroundColor: 'white',
    marginTop: '3px',
    paddingBottom: '8px',
    paddingTop: '6px',
    overflow: 'visible',
    gap: '.5rem',
    '&:hover': {
      backgroundColor: 'white',
      outline: `2px solid ${tokens.colorBrandStroke1}`,
    },
    '&:active': {
      backgroundColor: 'white',
      outline: `2px solid ${tokens.colorBrandStroke1}`,
    },
  },
  selectedCard: {
    outline: `3px solid ${tokens.colorBrandStroke1} !important`,
    '&:hover': {
      outline: `3px solid ${tokens.colorBrandStroke1} !important`,
    },
    '&:active': {
      outline: `3px solid ${tokens.colorBrandStroke1} !important`,
    },
  },
});

/**
 * A reusable base card with optional "selected" state.
 * Use `onSelectionChange` to handle toggling.
 * Provide `ignoreClickSelectors` if you have child popups or controls that shouldn't toggle selection.
 */
const BaseCard: FC<TBaseCardProps> = ({
  children,
  selectable = false,
  selected = false,
  onSelectionChange,
  ignoreClickSelectors = [],
  className,
  ...rest
}) => {
  const classes = useStyles();

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    if (!selectable || !onSelectionChange) return;

    const target = event.target as HTMLElement;
    // If the click is inside any of the ignored selectors, do not toggle
    for (const selector of ignoreClickSelectors) {
      if (target.closest(selector)) {
        // It's inside an ignored element => don't toggle selection
        return;
      }
    }
    onSelectionChange(!selected, event);
  };

  return (
    <Card
      className={mergeClasses(classes.baseCard, selected && classes.selectedCard, className)}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </Card>
  );
};

export { BaseCard };
